'use client';

import React, { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { gatewayClient } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { useErrorMessage } from '../../hooks/useErrorMessage';
import type { BankAccount } from '../../types/bankAccount';
import styles from './Cart.module.css';

interface PayerDetailsPanelProps {
  selectedAccountId: string | null;
  // null = nothing selectable (yet); parent blocks checkout until non-null.
  onSelectAccount: (id: string | null) => void;
}

// Fast local sanity check so obvious typos don't cost a round-trip. The
// backend re-validates authoritatively (ibantools) - this is UX only.
const looksLikeIban = (raw: string): boolean => {
  const iban = raw.replace(/\s+/g, '').toUpperCase();
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban)) return false;
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  const numeric = rearranged.replace(/[A-Z]/g, (c) =>
    String(c.charCodeAt(0) - 55),
  );
  let remainder = 0;
  for (const digit of numeric) {
    remainder = (remainder * 10 + Number(digit)) % 97;
  }
  return remainder === 1;
};

// SEPA payer-details block, shown under the payment tiles while bank
// transfer is selected. Owns: profile name save, the saved-accounts list,
// and the add-account form. Does NOT own the selected account id - that
// lives on the parent because useCartLogic sends it on checkout. Collecting
// this BEFORE checkout is deliberate: the backend refuses to challenge any
// OTP until the payer details exist, so an abandoned form costs nothing.
const PayerDetailsPanel: React.FC<PayerDetailsPanelProps> = ({
  selectedAccountId,
  onSelectAccount,
}) => {
  const t = useTranslations('cart');
  const resolveError = useErrorMessage();
  const { profile, refreshProfile } = useAuth();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [nameError, setNameError] = useState('');
  const namePrefilled = useRef(false);

  const [accounts, setAccounts] = useState<BankAccount[] | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [holderName, setHolderName] = useState('');
  const [iban, setIban] = useState('');
  const [accountError, setAccountError] = useState('');
  const [busy, setBusy] = useState(false);

  // Prefill the name once the profile arrives; never overwrite user typing.
  useEffect(() => {
    if (namePrefilled.current || !profile) return;
    namePrefilled.current = true;
    setFirstName(profile.firstName ?? '');
    setLastName(profile.lastName ?? '');
  }, [profile]);

  useEffect(() => {
    let cancelled = false;
    gatewayClient.bankAccounts
      .list()
      .then((list) => {
        if (cancelled) return;
        setAccounts(list);
        // Auto-open only, never auto-close: the user may have opened the
        // add form while this fetch was still in flight.
        if (list.length === 0) {
          setShowAddForm(true);
        }
      })
      .catch((error) => {
        if (cancelled) return;
        // Don't fake an empty list silently - a returning customer would be
        // pushed into re-adding (and possibly hitting the account cap).
        setAccounts([]);
        setShowAddForm(true);
        setAccountError(resolveError(error));
      });
    return () => {
      cancelled = true;
    };
    // resolveError is render-stable enough for a mount-only fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-select ONLY when nothing is selected (so a returning customer
  // clicks nothing). A stale selection is left alone on purpose: selecting
  // clears any pending payer-gate error, so "fixing" it here would silently
  // swallow a server rejection the user was just shown. A stale id fails
  // checkout with a visible, recoverable error instead.
  useEffect(() => {
    if (!accounts || selectedAccountId !== null || !accounts[0]) return;
    onSelectAccount(accounts[0].id);
  }, [accounts, selectedAccountId, onSelectAccount]);

  // The backend requires a profile name for SEPA (it's the baseline the payer
  // holder name is risk-compared against). Saved on blur, silently.
  const saveNameIfChanged = async () => {
    const first = firstName.trim();
    const last = lastName.trim();
    if (!first || !last) return;
    if (first === (profile?.firstName ?? '') && last === (profile?.lastName ?? '')) {
      return;
    }
    try {
      setNameError('');
      await gatewayClient.auth.updateProfile({ firstName: first, lastName: last });
      await refreshProfile();
    } catch (error) {
      setNameError(resolveError(error));
    }
  };

  const openAddForm = () => {
    setAccountError('');
    if (!holderName.trim()) {
      setHolderName(`${firstName.trim()} ${lastName.trim()}`.trim());
    }
    setShowAddForm(true);
  };

  const handleAddAccount = async () => {
    if (busy) return;
    const cleanHolder = holderName.trim();
    if (!cleanHolder || cleanHolder.split(/\s+/).length < 2) {
      setAccountError(resolveError({ errorCode: 'BANK_ACCOUNT_INVALID_HOLDER_NAME' }));
      return;
    }
    if (!looksLikeIban(iban)) {
      setAccountError(resolveError({ errorCode: 'BANK_ACCOUNT_INVALID_IBAN' }));
      return;
    }

    setBusy(true);
    setAccountError('');
    try {
      const created = await gatewayClient.bankAccounts.add(cleanHolder, iban.trim());
      setAccounts((prev) => {
        const rest = (prev ?? []).filter((a) => a.id !== created.id);
        return [created, ...rest];
      });
      onSelectAccount(created.id);
      setShowAddForm(false);
      setIban('');
    } catch (error) {
      setAccountError(resolveError(error));
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveAccount = async (id: string) => {
    if (busy) return;
    setBusy(true);
    setAccountError('');
    try {
      await gatewayClient.bankAccounts.remove(id);
      const remaining = (accounts ?? []).filter((a) => a.id !== id);
      setAccounts(remaining);
      // Deleting the selected account hands the selection to the next one
      // (deliberate user action - unlike the auto-select effect, nothing
      // pending can be swallowed here).
      if (id === selectedAccountId) {
        onSelectAccount(remaining[0]?.id ?? null);
      }
      // Deleting the last account should land on the add form, not a bare
      // "use another account" link.
      if (remaining.length === 0) {
        openAddForm();
      }
    } catch (error) {
      setAccountError(resolveError(error));
    } finally {
      setBusy(false);
    }
  };

  const hasAccounts = (accounts?.length ?? 0) > 0;

  return (
    <div className={styles.payerPanel}>
      <div className={styles.balancePanelHeader}>
        <strong>{t('summary.payerDetails.title')}</strong>
      </div>
      <div className={styles.payerHint}>{t('summary.payerDetails.hint')}</div>

      <div className={styles.payerNameRow}>
        <div className={styles.payerField}>
          <label className={styles.payerLabel} htmlFor="payer-first-name">
            {t('summary.payerDetails.firstName')}
          </label>
          <input
            id="payer-first-name"
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            onBlur={() => void saveNameIfChanged()}
            autoComplete="given-name"
            className={styles.payerInput}
          />
        </div>
        <div className={styles.payerField}>
          <label className={styles.payerLabel} htmlFor="payer-last-name">
            {t('summary.payerDetails.lastName')}
          </label>
          <input
            id="payer-last-name"
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            onBlur={() => void saveNameIfChanged()}
            autoComplete="family-name"
            className={styles.payerInput}
          />
        </div>
      </div>
      {nameError && <div className={styles.errorMessage}>{nameError}</div>}

      {hasAccounts && (
        <div className={styles.payerAccounts} role="radiogroup" aria-label={t('summary.payerDetails.title')}>
          {accounts!.map((account) => (
            <div
              key={account.id}
              className={`${styles.payerAccountRow} ${
                selectedAccountId === account.id ? styles.payerAccountRowActive : ''
              }`}
            >
              {/* Delete stays a SIBLING of the label - a button inside it
                  would be read out as part of the radio's accessible name. */}
              <label className={styles.payerAccountLabel}>
                <input
                  type="radio"
                  name="payerBankAccount"
                  checked={selectedAccountId === account.id}
                  onChange={() => onSelectAccount(account.id)}
                  className={styles.payerAccountRadio}
                />
                <span className={styles.payerAccountText}>
                  <span className={styles.payerAccountHolder}>{account.holderName}</span>
                  <span className={styles.payerAccountIban}>
                    {account.ibanMasked} · {account.country}
                  </span>
                </span>
              </label>
              <button
                type="button"
                onClick={() => void handleRemoveAccount(account.id)}
                disabled={busy}
                className={styles.payerAccountDelete}
                aria-label={t('summary.payerDetails.delete')}
                title={t('summary.payerDetails.delete')}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {showAddForm ? (
        <div className={styles.payerAddForm}>
          <div className={styles.payerField}>
            <label className={styles.payerLabel} htmlFor="payer-holder-name">
              {t('summary.payerDetails.holderName')}
            </label>
            <input
              id="payer-holder-name"
              type="text"
              value={holderName}
              onChange={(e) => setHolderName(e.target.value)}
              autoComplete="name"
              className={styles.payerInput}
            />
          </div>
          <div className={styles.payerField}>
            <label className={styles.payerLabel} htmlFor="payer-iban">
              {t('summary.payerDetails.iban')}
            </label>
            <input
              id="payer-iban"
              type="text"
              value={iban}
              onChange={(e) => setIban(e.target.value)}
              placeholder={t('summary.payerDetails.ibanPlaceholder')}
              autoComplete="off"
              spellCheck={false}
              className={styles.payerInput}
            />
          </div>
          <button
            type="button"
            onClick={() => void handleAddAccount()}
            disabled={busy}
            className={styles.payerSaveButton}
          >
            {busy ? t('summary.payerDetails.saving') : t('summary.payerDetails.save')}
          </button>
        </div>
      ) : (
        <button type="button" onClick={openAddForm} className={styles.payerAddLink}>
          + {t('summary.payerDetails.addNew')}
        </button>
      )}
      {accountError && <div className={styles.errorMessage}>{accountError}</div>}
    </div>
  );
};

export default PayerDetailsPanel;
